import {Button, Tooltip} from 'antd';
import cn from 'classnames';
import React, {useEffect, ComponentType} from 'react';
import {
    DragDropContext,
    Droppable,
} from 'react-beautiful-dnd';
import {useHistory} from 'react-router-dom';

import {DynamicIcon, IconsMap, TIconType} from 'components/dynamic-icon';
import {IDictionary} from 'components/personal-account/tabs/dashboards/dashboards-types';
import {Spinner} from 'components/spinner';
import {selectContextRawData} from 'modules/context/context-selector';
import {WidgetSettings} from 'pages/widget-page/widget-settings';
import {useDraggableComponents} from 'shared/hooks/use-draggable-components';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {
    useLazyFetchAllDashboardsSettingsQuery,
    useLazyFetchAllFavoriteDashboardsQuery,
} from 'store/api/settings-dashboards/settings-dashboards.api';
import {useAppSelector} from 'store/config/hooks';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {
    checkIfAllColumnsHaveOneWidget,
    getNumberOfMaxWidgetsPerColumn,
    widgetDraggableComponentsInitialState,
    widgetPageComponentMap,
} from './constants/widget-page.constants';
import {EmptyWidgetsGreetings} from './empty-widgets-greetings/empty-widgets-greetings';
import {WidgetDraggableComponent} from './widget-draggable-component/widget-draggable-component';

import './widget-page.less';

export const WIDGET_PAGE_ENTITY_NAME = 'widgets';
export const WIDGET_PAGE_SECTION_NAME = 'app.main.page.fvs.menu';
export const SWAP_AREA_CLASSNAME = '.widget-page__widget__swap-area.open';

const dashboardDictionary: IDictionary<string> = {
    'dashboard.item.request': 'REQUEST_TABLE',
    'dashboard.item.publication.doc': 'DOC_JOURNAL_PIE_CHART',
    'dashboard.item.components.nob': 'NOB_COMPONENTS_BAR_CHART',
    'dashboard.item.news': 'NEWS',
};

export const WidgetPage = () => {
    const history = useHistory();
    const contextRawData = useAppSelector(selectContextRawData);

    const [dashboardsTrigger, dashboards] = useLazyFetchAllDashboardsSettingsQuery({});
    const [favoritesTrigger, favorites] = useLazyFetchAllFavoriteDashboardsQuery({});

    const sections = favorites.data?.filter(item => item?.parentEntityName !== 'dashboard.item') ?? [];

    const {
        components: widgets,
        handleDragStart,
        handleDragEnd,
        isDragging,
        draggingId,
    } = useDraggableComponents(
        widgetDraggableComponentsInitialState,
        WIDGET_PAGE_ENTITY_NAME,
        SWAP_AREA_CLASSNAME,
    );

    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: [WIDGET_PAGE_SECTION_NAME],
    });

    const areDashboardsVisible = hasUserRoleFunction(EUserRoleFunction.VIEW);
    const isEditEnabled = hasUserRoleFunction(EUserRoleFunction.UPDATE);

    const maxWidgetsPerColumn = getNumberOfMaxWidgetsPerColumn(widgets);
    const allColumnsHaveOneWidget = checkIfAllColumnsHaveOneWidget(widgets);

    const activeDashboards = favorites.data?.map(item => (
        item.isFavorite ? dashboardDictionary[item.entityName] : '')) ?? [];

    const isLogoPlaceholderVisible = (sections?.length <= 0 && activeDashboards?.length <= 0) || !areDashboardsVisible;

    const isLoading = dashboards.isLoading || favorites.isLoading;

    const getSectionIcon = (sectionIconName?: string) => {
        const iconsMapping = IconsMap as {[index: string]: ComponentType};
        if (sectionIconName && iconsMapping[sectionIconName]) {
            return (
                <DynamicIcon
                    className="section-icon"
                    type={sectionIconName as TIconType}
                />
            );
        }
        return null;
    };

    const updateData = () => {
        dashboardsTrigger(null);
        favoritesTrigger(null);
    };

    useEffect(() => {
        updateData();
    }, []);

    return (
        isLoading ? (
            <Spinner />
        ) : (
            <div className="widget-page">
                <DragDropContext
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    {areDashboardsVisible && (
                        <div className="widget-page__header">
                            <h1>Главная страница</h1>
                            <WidgetSettings
                                dashboardsSettings={dashboards?.data}
                                favorites={favorites?.data}
                                updateData={updateData}
                                isEditEnabled={isEditEnabled}
                            />
                        </div>
                    )}

                    {isLogoPlaceholderVisible ? (
                        <EmptyWidgetsGreetings />
                    ) : (
                        <>
                            {!!sections?.length && (
                                <div className="widget-page__sections">
                                    {sections?.map(section => (
                                        <div className="widget-page__section-block">
                                            <Tooltip title={`${section.parentName} - ${section.name}`}>
                                                <Button
                                                    className={cn(
                                                        'widget-page__section-button',
                                                        section.icon && 'widget-page__section-button--with-icon',
                                                    )}
                                                    onClick={() => history.push(`/${section.entityName}`)}
                                                    type="primary"
                                                >
                                                    {getSectionIcon(section.icon)}
                                                    {`${section.parentName} - ${section.name}`}
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className={cn(
                                'widget-page__widgets-container',
                                !sections?.length && 'full-width',
                            )}>
                                {widgets.map((colWidgets, droppableId) => (
                                    <Droppable
                                        droppableId={droppableId.toString()}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={cn('widget-block', snapshot.isDraggingOver && 'dragging')}
                                            >
                                                {colWidgets.map(({
                                                    id,
                                                    component,
                                                    entityName,
                                                }, idx) => (
                                                    activeDashboards?.includes(component) ? (
                                                        <div className="widget-page__widget__wrapper">
                                                            <div
                                                                className={cn('widget-page__widget__swap-area', {
                                                                    open: isDragging && id !== draggingId,
                                                                })}
                                                                id={id}
                                                            >
                                                                <DynamicIcon
                                                                    className='widget-page__widget__swap-icon'
                                                                    type='SwapOutlined'
                                                                />
                                                            </div>
                                                            <WidgetDraggableComponent
                                                                component={widgetPageComponentMap[component]}
                                                                draggableId={id}
                                                                index={idx}
                                                                componentProps={{contextRawData, entityName}}
                                                                autoAdjustHeight={
                                                                    !snapshot.isDraggingOver
                                                                    && (colWidgets.length < maxWidgetsPerColumn
                                                                        || allColumnsHaveOneWidget)
                                                                }
                                                            />
                                                        </div>
                                                    ) : (<></>)
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                ))}
                            </div>
                        </>
                    )}

                </DragDropContext>
            </div>
        )
    );
};
